import { useState } from 'react';
import { Zap, Copy, Loader2, Check } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { useZapFlow, fetchZapInvoice, useWebLn } from '@/hooks/useZapFlow';
import { useZapTotal } from '@/hooks/useZapTotal';
import type { NostrEvent } from '@nostrify/nostrify';

const ZAP_PRESETS = [21, 210, 2100];

interface ZapButtonProps {
  event: NostrEvent;
}

/** NIP-57 zap button: creates a signed 9734 zap request, fetches invoice, offers payment. */
export function ZapButton({ event }: ZapButtonProps) {
  const { user } = useCurrentUser();
  const zapTotalData = useZapTotal(event);
  const totalSats = zapTotalData.data?.total ?? 0;
  const unverifiedTotal = zapTotalData.data?.unverifiedTotal ?? 0;
  const unverifiedCount = zapTotalData.data?.unverifiedCount ?? 0;
  const { hasWebln, pay } = useWebLn();

  const [open, setOpen] = useState(false);
  const [amount, setAmount] = useState<number>(21);
  const [invoice, setInvoice] = useState<string | undefined>();
  const [paid, setPaid] = useState(false);
  const [error, setError] = useState<string | undefined>();
  const [working, setWorking] = useState(false);

  const zapFlow = useZapFlow(event);

  const startZap = async (sats: number) => {
    if (!user || !zapFlow.data) return;
    setWorking(true);
    setError(undefined);
    setInvoice(undefined);
    setPaid(false);
    try {
      // 1. Sign zap request (NOT published to relays)
      const zapRequest = await user.signer.signEvent({
        kind: 9734,
        content: '',
        tags: [
          ['relays', 'wss://relay.ditto.pub', 'wss://relay.damus.io', 'wss://relay.dreamith.to'],
          ['amount', String(sats * 1000)],
          ['p', event.pubkey],
          ['e', event.id],
          ['k', String(event.kind)],
        ],
      });

      // 2. Fetch invoice from wallet callback
      const pr = await fetchZapInvoice(
        zapFlow.data.pay.callback,
        zapRequest,
        sats * 1000,
        zapFlow.data.lud16,
      );
      setInvoice(pr);

      // 3. Try WebLN auto-pay
      if (hasWebln) {
        try {
          await pay(pr);
          setPaid(true);
        } catch {
          // user declined or failed — keep invoice visible
        }
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Zap failed');
    } finally {
      setWorking(false);
    }
  };

  const recipientHasLightning = Boolean(zapFlow.data);

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="text-[12px] text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-gray-100 inline-flex items-center gap-0.5"
        aria-label={`Zap this ${recipientHasLightning ? '' : '(recipient has no lightning address)'}`}
      >
        <Zap className="size-3 text-[#f97316] dark:text-[#fb923c]" fill="currentColor" />
        {totalSats ?? 0} sats
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="text-[16px]">⚡ Zap {event.pubkey.slice(0, 8)}…</DialogTitle>
          </DialogHeader>

          {!user ? (
            <p className="text-[13px] text-gray-600 dark:text-gray-400">Log in to zap.</p>
          ) : !recipientHasLightning ? (
            <p className="text-[13px] text-gray-600 dark:text-gray-400">
              This user has no lightning address. Use coins below.
            </p>
          ) : (
            <div className="space-y-3">
              {unverifiedCount > 0 && (
                <p className="text-[11px] text-amber-600 dark:text-amber-400">
                  Includes {unverifiedTotal} sats from {unverifiedCount} receipt
                  {unverifiedCount === 1 ? '' : 's'} without recipient tag.
                </p>
              )}
              <div className="flex gap-2">
                {ZAP_PRESETS.map((sats) => (
                  <Button
                    key={sats}
                    type="button"
                    variant={amount === sats ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setAmount(sats)}
                    className={cnPresets(amount === sats)}
                  >
                    {sats}
                  </Button>
                ))}
              </div>

              {working && (
                <div className="flex items-center gap-2 text-[13px] text-gray-600 dark:text-gray-400">
                  <Loader2 className="size-4 animate-spin" /> Requesting invoice…
                </div>
              )}

              {error && <p className="text-[12px] text-red-600 dark:text-red-400">{error}</p>}

              {invoice && (
                <div className="space-y-2">
                  <p className="text-[12px] text-gray-600 dark:text-gray-400 break-all bg-gray-50 dark:bg-[#1a1c1e] border border-gray-200 dark:border-[#2a2d30] rounded p-2">
                    {invoice}
                  </p>
                  <div className="flex gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => navigator.clipboard.writeText(invoice)}
                      className="text-[12px]"
                    >
                      <Copy className="size-3.5 mr-1" /> Copy invoice
                    </Button>
                    {hasWebln && !paid && (
                      <Button
                        type="button"
                        size="sm"
                        onClick={() => pay(invoice).then(() => setPaid(true)).catch(() => {})}
                        className="text-[12px] bg-[#f97316] hover:bg-[#ea580c] dark:bg-[#fb923c] dark:hover:bg-[#fdba74] dark:text-[#431407] text-white"
                      >
                        Pay with wallet
                      </Button>
                    )}
                    {paid && (
                      <span className="text-[12px] text-green-600 dark:text-green-400 inline-flex items-center gap-1">
                        <Check className="size-3.5" /> Paid!
                      </span>
                    )}
                  </div>
                </div>
              )}

              {!invoice && !working && (
                <Button
                  type="button"
                  onClick={() => startZap(amount)}
                  className="w-full bg-[#f97316] hover:bg-[#ea580c] dark:bg-[#fb923c] dark:hover:bg-[#fdba74] dark:text-[#431407] text-white text-[13px]"
                >
                  <Zap className="size-4 mr-1" fill="currentColor" /> Zap {amount} sats
                </Button>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}

function cnPresets(active: boolean): string {
  return active
    ? 'bg-[#f97316] text-white border-[#f97316] dark:bg-[#fb923c] dark:border-[#fb923c] dark:text-[#431407]'
    : '';
}
